import { Grid } from '@material-ui/core';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { authLogout } from '../../auth/authSlice';
import { formatarCpf, formatarNit, formatarCompetenciaTipo2 } from '../../common/formatadores';
import Accordion, { SCROLL_OFFSET_NAV_FIXED } from '../../components/Accordion/Accordion';
import AjudaRapida from '../../components/AjudaRapida/AjudaRapida';
import Card from '../../components/Card/Card';
import FieldGroup from '../../components/FieldGroup/FieldGroup';
import MainContainer from '../../components/MainContainer/MainContainer';
import Message from '../../components/Message/Message';
import { selectPerfilCompleto, selectSituacaoCadastroFamilia } from '../Perfil/perfilSlice';
import styles from './ConsultaEscolaridade.module.css';
import {
  IntegrantePovoamentoEscolaridade,
  obterIntegrantesPovoamento,
  selectIntegrantesPovoamento,
} from './consultaEscolaridadeSlice';
import { obterNomeDoPerfil } from '../../common/perfilUtils';
import { obterNomeOuNomeSocial } from '../../common/integranteUtils';
import { trackServiceSelect } from '../../common/tracker';

const ConsultaEscolaridade: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();

  const perfil = useAppSelector(selectPerfilCompleto);
  const situacaoCadastroFamilia = useAppSelector(selectSituacaoCadastroFamilia);

  const integrantesPovoamento = useAppSelector(selectIntegrantesPovoamento);

  useEffect(() => {
    dispatch(obterIntegrantesPovoamento(perfil.numeroFamiliar));
  }, [dispatch, perfil.numeroFamiliar]);

  const mostrarParentesco = (tipoRelacaoParentescoRF: any) => {
    if (tipoRelacaoParentescoRF?.nome === 'Pessoa Responsável pela Unidade Familiar - RF') {
      return 'Responsável Familiar - RF';
    }
    return tipoRelacaoParentescoRF?.nome;
  };

  const checarEscolaridadeFamilia = () => {
    let temEscolaridadeFamilia = false;
    if (integrantesPovoamento && integrantesPovoamento.length > 0) {
      // Loop pra checar se algum integrante possui a flag temEscolaridade = true;
      integrantesPovoamento.every(integrante => {
        if (integrante.temEscolaridade) {
          temEscolaridadeFamilia = true;
          return false;
        }
        return true;
      });
    }
    return temEscolaridadeFamilia;
  };

  const caixaMensagens = () => {
      trackServiceSelect('POR-MENU', "CAIXA DE MENSAGEM");
      history.push('/caixa-mensagem');
    };

  const dadosIntegrante = (integrante: IntegrantePovoamentoEscolaridade) => {
    return (
      <>
        <Grid container spacing={2} style={{marginBottom: 20}}>
          <Grid item xs={12}>
            <FieldGroup
              label="Parentesco com o(a) RF"
              value={mostrarParentesco(integrante.tipoRelacaoParentescoRF)}
            />
          </Grid>
          <Grid item xs={6}>
            <FieldGroup
              label="Estado cadastral"
              value={integrante.estadoCadastralMembroDTO?.nome}
            />
          </Grid>
          <Grid item xs={6}>
            <FieldGroup
              label="NIS"
              value={formatarNit(integrante.nis as any)}
            />
          </Grid>
          <Grid item xs={12}>
            <FieldGroup
              label="CPF"
              value={formatarCpf(integrante.cpf)}
            />
          </Grid>
        </Grid>

        <div className={styles.fieldSetHeader}>
          <i className={`fas fa-book-reader`}></i>
          <span>Escolaridade</span>
        </div>
        <Card className={styles.fieldSet}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FieldGroup
                label="Mês da atualização"
                value={integrante.competencia ? formatarCompetenciaTipo2(integrante.competencia) : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldGroup
                label="Código do INEP/MEC da escola ou creche"
                value={`${integrante.codigoInepEscola} - ${integrante.nomeInepEscola}`}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldGroup
                label="Curso que frequenta"
                value={integrante.cursoFrequentado}
              />
            </Grid>
            <Grid item xs={12}>
              <FieldGroup
                label="Ano/Série que frequenta"
                value={integrante.anoSerieFrequentada}
              />
            </Grid>
          </Grid>
        </Card>
      </>
    )
  };

  return (
    <MainContainer
      userNav
      navTitle={obterNomeDoPerfil(perfil, situacaoCadastroFamilia)}
      titleBarraAuxiliar={'Consulta de escolaridade atualizada'}
      barraAuxiliar
      stoppedTitle
      navFixed
      navCaixaMensagens={() => caixaMensagens()}
      navLogout={() => dispatch(authLogout({ history }))}
      navSeparator
    >
      <div className={styles.container}>
        {!checarEscolaridadeFamilia() && (
          <Message
            type="info"
            className={styles.message}
            style={{ marginBottom: 20 }}
            text={`
              Atualização automática de escolaridade não foi identificada para esta família.
            `}
          />
        )}
        {checarEscolaridadeFamilia() && (
          <>
            <div className={styles.cardHeader}>
              <i className={`fas fa-address-book ${styles.cardIcon}`}></i>
              <span className={styles.cardHeaderTitle}>
                {integrantesPovoamento?.length} integrante{integrantesPovoamento?.length > 1 ? 's' : ''} da família
              </span>
            </div>
            {integrantesPovoamento.map((integrante, index) => {
              return (
                <Accordion
                  id={`membro-${index}`}
                  title={obterNomeOuNomeSocial(integrante)}
                  subtitle={mostrarParentesco(integrante.tipoRelacaoParentescoRF)}
                  exibirSubtitulo={!integrante.temEscolaridade}
                  key={index}
                >
                  {integrante.temEscolaridade && dadosIntegrante(integrante)}
                  {!integrante.temEscolaridade && (
                    <Message
                      type="warning"
                      className={styles.message}
                      style={{ marginTop: -10 }}
                      text={`
                        Atualização automática de escolaridade não identificada para o
                        integrante familiar.
                      `}
                    />
                  )}
                </Accordion>
              );
            })}
          </>
        )}
        <AjudaRapida scrollOffset={SCROLL_OFFSET_NAV_FIXED}>
          <div className={styles.textoAjudaRapida}>
            <strong>Consulta de escolaridade atualizada</strong>
            <br/>
            <br/>
            Aqui você pode consultar se houve uma atualização automática realizada
            pelo Governo Federal, dos dados de escolaridade, para pessoas da família
            que tenham entre 04 e 17 anos.
            <br/>
            <br/>
            Por enquanto, a atualização automática dos dados de escolaridade será
            apenas para famílias beneficiárias do Programa Bolsa Família.
          </div>
        </AjudaRapida>
      </div>
    </MainContainer>
  );
};

export default ConsultaEscolaridade;
